import { useState } from 'react';

import { ClockIcon } from '../components/icons';

/**
 * When a snoozed message comes back, sensible at any hour:
 *
 * - in an hour, always;
 * - later today (in three hours) while that lands in the day, 09:00–20:00;
 * - the coming morning at 08:00 — "this morning" when it is still night
 *   (before 05:00), so a message snoozed at 01:00 is back when the person is
 *   up, not a day and a half later;
 * - Monday morning, unless the coming morning already is Monday.
 *
 * 08:00 is the start of the working day Outlook and Gmail use for theirs.
 */
export function snoozePresets(now = new Date()): Array<[string, Date]> {
  const after = (hours: number) => {
    const d = new Date(now);
    d.setHours(d.getHours() + hours, d.getMinutes(), 0, 0);
    return d;
  };
  const morning = (days: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    d.setHours(8, 0, 0, 0);
    return d;
  };
  const list: Array<[string, Date]> = [['In an hour', after(1)]];
  const later = after(3);
  if (later.getDate() === now.getDate() && later.getHours() >= 9 && later.getHours() < 20) {
    list.push(['Later today', later]);
  }
  const night = now.getHours() < 5;
  const coming = night ? morning(0) : morning(1);
  list.push([night ? 'This morning' : 'Tomorrow morning', coming]);
  const monday = morning((8 - now.getDay()) % 7 || 7);
  if (monday.toDateString() !== coming.toDateString()) {
    list.push(['Monday morning', monday]);
  }
  return list;
}

export const whenLabel = (at: Date) =>
  at.toLocaleString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/** The reader's Snooze button and its menu of times. */
export function SnoozeButton({ onSnooze }: { onSnooze: (until: Date) => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);
  const choose = (at: Date) => {
    if (Number.isNaN(at.getTime()) || at.getTime() < Date.now() + 60_000) {
      setError('Choose a time at least a minute from now.');
      return;
    }
    setOpen(false);
    setError(null);
    onSnooze(at);
  };
  return (
    <span className="later">
      <button
        type="button"
        className="icon-btn"
        title="Snooze"
        aria-label="Snooze"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <ClockIcon />
      </button>
      {open && (
        <div className="later-menu down" role="menu" aria-label="Snooze until">
          <strong>Snooze until</strong>
          {snoozePresets().map(([label, at]) => (
            <button key={label} type="button" role="menuitem" onClick={() => choose(at)}>
              <span>{label}</span>
              <span className="muted">{whenLabel(at)}</span>
            </button>
          ))}
          <label className="later-pick">
            <span>Choose a date and time</span>
            <input type="datetime-local" value={pick} onChange={(e) => setPick(e.target.value)} />
          </label>
          <button
            type="button"
            className="btn"
            style={{ width: 'auto' }}
            disabled={!pick}
            onClick={() => choose(new Date(pick))}
          >
            Snooze until then
          </button>
          {error && <span className="error">{error}</span>}
        </div>
      )}
    </span>
  );
}
